import { useNavigate } from "react-router-dom";
import {
    Box, Stack, Typography, Paper, IconButton,
    Table, TableBody, TableRow, TableCell, TableContainer, TableHead,
    CircularProgress, Chip,
} from "@mui/material";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import EventIcon from '@mui/icons-material/Event';
import StatCard from "./StatCard";
import helpers from "../../../hooks/HelperFunctions";

const HEAD_STYLES = {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.80)',
    fontFamily: 'Quicksand',
    letterSpacing: 0.8,
    background: 'linear-gradient(135deg, #1C1C2E 0%, #2D1A5E 100%)',
    borderBottom: 'none',
    py: 2.0,
};

const CELL_STYLES = {
    fontSize: 14,
    fontFamily: 'Quicksand',
    fontWeight: 600,
    color: '#374151',
    borderBottom: '1px solid rgba(0,0,0,0.04)',
    py: 2.0,
};

const ROW_HOVER = {
    '& td:first-of-type': {
        borderLeft: '3px solid transparent',
        transition: 'border-left-color 0.15s ease',
    },
    '&:hover': {
        bgcolor: '#FFF5F0',
        '& td:first-of-type': { borderLeftColor: '#FF5200' },
    },
    transition: 'background-color 0.15s ease',
};

function formatDate(date) {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
}

function CreatorPlanTab({ planData, isLoading, error }) {
    const navigate = useNavigate();
    const summary = planData?.summary;
    const rows = planData?.plans || [];

    if (isLoading) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
                <CircularProgress size={30} sx={{ color: '#FF5200' }} />
            </Stack>
        );
    }

    if (error) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 8, gap: 1.5 }}>
                <Box
                    sx={{
                        width: 64, height: 64, borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(255,82,0,0.07) 0%, rgba(255,140,0,0.04) 100%)',
                        border: '2px dashed rgba(255,82,0,0.20)',
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: 26,
                    }}
                >
                    ⚠️
                </Box>
                <Typography sx={{ fontFamily: 'Quicksand', fontWeight: 800, color: '#1C1C2E', fontSize: 15 }}>
                    Could not load plans
                </Typography>
                <Typography sx={{ fontFamily: 'Quicksand', color: '#6B7280', fontSize: 13, maxWidth: 280, textAlign: 'center' }}>
                    Please try again in a moment.
                </Typography>
            </Stack>
        );
    }

    if (!rows.length) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 8, gap: 1.5 }}>
                <Box
                    sx={{
                        width: 64, height: 64, borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(255,82,0,0.07) 0%, rgba(255,140,0,0.04) 100%)',
                        border: '2px dashed rgba(255,82,0,0.20)',
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'center', fontSize: 26,
                    }}
                >
                    📋
                </Box>
                <Typography sx={{ fontFamily: 'Quicksand', fontWeight: 800, color: '#1C1C2E', fontSize: 15 }}>
                    Not part of any plan yet
                </Typography>
                <Typography sx={{ fontFamily: 'Quicksand', color: '#6B7280', fontSize: 13, maxWidth: 280, textAlign: 'center' }}>
                    Plans this creator is added to will show up here.
                </Typography>
            </Stack>
        );
    }

    return (
        <Box>
            <Stack direction="row" sx={{ mt: 0 }} columnGap={2} flexWrap="wrap" rowGap={2}>
                <StatCard
                    heading="Total Plans"
                    value={`${summary?.totalPlans ?? 0}`}
                    icon={<ListAltIcon sx={{ fontSize: 18 }} />}
                    accentColor="#6366F1"
                />
                <StatCard
                    heading="Active Plans"
                    value={`${summary?.activePlans ?? 0}`}
                    icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                    accentColor="#10B981"
                />
                <StatCard
                    heading="Total Planned Cost"
                    value={helpers.formatNumber(summary?.totalPlannedCost || 0, true)}
                    icon={<CurrencyRupeeIcon sx={{ fontSize: 18 }} />}
                    accentColor="#FF5200"
                />
                <StatCard
                    heading="Last Added"
                    value={formatDate(summary?.lastAddedOn)}
                    icon={<EventIcon sx={{ fontSize: 18 }} />}
                    accentColor="#F59E0B"
                />
            </Stack>

            <Box
                sx={{
                    marginTop: 3,
                    borderRadius: 3,
                    overflow: 'hidden',
                    boxShadow: '0 2px 16px rgba(0,0,0,0.06)',
                    border: '1px solid rgba(255,82,0,0.08)',
                }}
            >
                <TableContainer component={Paper} sx={{ maxHeight: 420, overflowX: 'auto', boxShadow: 'none' }}>
                    <Table stickyHeader sx={{ minWidth: 700 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ ...HEAD_STYLES, minWidth: 48 }}>#</TableCell>
                                <TableCell sx={{ ...HEAD_STYLES, minWidth: 180 }}>Plan</TableCell>
                                <TableCell sx={{ ...HEAD_STYLES, minWidth: 140 }}>List</TableCell>
                                <TableCell sx={{ ...HEAD_STYLES, minWidth: 110 }}>Added On</TableCell>
                                <TableCell sx={{ ...HEAD_STYLES, minWidth: 110 }}>Planned Cost</TableCell>
                                <TableCell sx={{ ...HEAD_STYLES, minWidth: 90 }}>Status</TableCell>
                                <TableCell sx={{ ...HEAD_STYLES, minWidth: 80, textAlign: 'center' }}>Open</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r, index) => (
                                <TableRow key={`${r.planId}-${index}`} sx={ROW_HOVER}>
                                    <TableCell sx={{ ...CELL_STYLES, color: '#9CA3AF', fontSize: 12 }}>
                                        {index + 1}
                                    </TableCell>
                                    <TableCell
                                        title={r.planName}
                                        sx={{
                                            ...CELL_STYLES, fontWeight: 700, color: '#0F172A', maxWidth: 240,
                                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                        }}
                                    >
                                        {r.planName}
                                        {r.brand && (
                                            <Typography component="span" sx={{ fontSize: 11.5, color: '#9CA3AF', fontWeight: 600, ml: 0.75 }}>
                                                · {r.brand}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={CELL_STYLES}>{r.listName || '—'}</TableCell>
                                    <TableCell sx={CELL_STYLES}>{formatDate(r.addedOn)}</TableCell>
                                    <TableCell sx={{ ...CELL_STYLES, color: '#FF5200', fontWeight: 800 }}>
                                        {r.deliverableCost > 0 ? helpers.formatNumber(r.deliverableCost, true) : '—'}
                                    </TableCell>
                                    <TableCell sx={CELL_STYLES}>
                                        <Chip
                                            label={r.isArchived ? 'Archived' : 'Active'}
                                            size="small"
                                            sx={{
                                                height: 22, fontSize: 11, fontWeight: 700, fontFamily: 'Quicksand',
                                                bgcolor: r.isArchived ? '#F3F4F6' : 'rgba(16,185,129,0.10)',
                                                color: r.isArchived ? '#6B7280' : '#059669',
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ ...CELL_STYLES, textAlign: 'center' }}>
                                        <IconButton
                                            title="Open plan"
                                            size="small"
                                            onClick={() => navigate(`/plans-summary/${r.planId}`)}
                                            sx={{ color: '#FF5200', '&:hover': { bgcolor: 'rgba(255,82,0,0.08)' } }}
                                        >
                                            <OpenInNewIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    );
}

export default CreatorPlanTab;
